import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ListOrdered, Loader2, RefreshCw, Save, Play } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const API = process.env.REACT_APP_BACKEND_URL;

/** Admin → Εργαλεία: Εκθέματα δικασίμων από SOLON (TrackDocket) — ρυθμίσεις, ζωντανή λίστα Διαδικασιών, άντληση τώρα. */
const DocketSettingsCard = () => {
  const [st, setSt] = useState(null);
  const [procs, setProcs] = useState(null);
  const [sel, setSel] = useState({});
  const [days, setDays] = useState(10);
  const [hour, setHour] = useState(6);
  const [busy, setBusy] = useState(false);
  const [touched, setTouched] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/admin/solon/dockets/settings`);
      setSt(r.data); setDays(r.data.days_before); setHour(r.data.run_hour);
      const m = {}; (r.data.procedures || []).forEach((p) => { m[p.code] = p.label; }); setSel(m); setTouched(false);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (st?.status !== "running") return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [st?.status]);

  const loadProcs = async () => {
    setBusy(true);
    try { setProcs((await axios.get(`${API}/api/admin/solon/dockets/procedures`)).data.procedures || []); }
    catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα SOLON"); }
    finally { setBusy(false); }
  };
  const save = async (enabled) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/api/admin/solon/dockets/settings`, {
        enabled, days_before: parseInt(days, 10) || 10, run_hour: parseInt(hour, 10) || 6,
        procedures: Object.entries(sel).map(([code, label]) => ({ code, label })),
      });
      setSt(r.data); setTouched(false); toast.success("Οι ρυθμίσεις εκθεμάτων αποθηκεύτηκαν");
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setBusy(false); }
  };
  const run = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/admin/solon/dockets/run`); toast.success("Ξεκίνησε η άντληση εκθεμάτων από το SOLON"); setTimeout(load, 1500); }
    catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
    finally { setBusy(false); }
  };

  const sum = st?.last_run_summary;
  return (
    <Card data-testid="docket-settings-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2"><ListOrdered className="w-4 h-4 text-sky-700" />Εκθέματα Δικασίμων από SOLON.GOV (TrackDocket)</CardTitle>
        <CardDescription>Για κάθε δικάσιμο, {st?.days_before ?? 10} ημέρες πριν (και κάθε μέρα μέχρι τη δικάσιμο) αντλείται αυτόματα το Έκθεμα Ανάρτησης του πινακίου από το extapps.solon.gov.gr και είναι διαθέσιμο στη σελίδα «Εκθέματα SOLON». Οι γραμμές αντιστοιχίζονται με τις υποθέσεις του συστήματος (ΓΑΚ/έτος). Κατάστημα: {st?.court_office_label || "…"}.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 font-medium">
            <input type="checkbox" checked={!!st?.enabled} onChange={(e) => save(e.target.checked)} disabled={busy || !st} data-testid="docket-enabled-toggle" />
            Αυτόματη άντληση εκθεμάτων
          </label>
          <div className="flex items-center gap-1 text-xs"><span>Ημέρες πριν τη δικάσιμο</span>
            <Input type="number" min={1} max={15} value={days} onChange={(e) => { setDays(e.target.value); setTouched(true); }} className="w-16 h-8" data-testid="docket-days-before" /></div>
          <div className="flex items-center gap-1 text-xs"><span>Ώρα ημερήσιας άντλησης</span>
            <Input type="number" min={0} max={23} value={hour} onChange={(e) => { setHour(e.target.value); setTouched(true); }} className="w-16 h-8" data-testid="docket-run-hour" /></div>
          <Button size="sm" variant="outline" onClick={run} disabled={busy || st?.status === "running"} data-testid="docket-run-now">
            {st?.status === "running" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}Άντληση τώρα
          </Button>
          <Button size="sm" variant="ghost" onClick={load} data-testid="docket-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <div className="text-xs text-slate-600" data-testid="docket-status">
          Κατάσταση: <b>{{ idle: "σε αναμονή", running: "άντληση σε εξέλιξη…", error: "σφάλμα" }[st?.status] || "…"}</b>
          {st?.last_run_at && <> · τελευταία άντληση: {new Date(st.last_run_at).toLocaleString("el-GR")}</>}
          {sum && <> · δικάσιμοι: <b>{sum.hearings}</b> · γραμμές: <b>{sum.lines}</b> · πινάκια: {sum.dockets} · διαδικασίες: {sum.procedures}</>}
          {st?.last_error && <span className="text-amber-700"> · {st.last_error}</span>}
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-medium">Διαδικασίες προς άντληση ({Object.keys(sel).length})
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={loadProcs} disabled={busy} data-testid="docket-load-procs">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Φόρτωση λίστας από SOLON"}</Button>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1 max-h-56 overflow-y-auto border rounded p-2" data-testid="docket-procs">
            {(procs || st?.procedures || []).map((p) => (
              <label key={p.code} className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={!!sel[p.code]} onChange={(e) => { const n = { ...sel }; if (e.target.checked) n[p.code] = p.label; else delete n[p.code]; setSel(n); setTouched(true); }} data-testid={`docket-proc-${p.code}`} />
                {p.label}
              </label>
            ))}
          </div>
        </div>
        {touched && <Button size="sm" onClick={() => save(!!st?.enabled)} disabled={busy} data-testid="docket-save"><Save className="w-4 h-4 mr-1" />Αποθήκευση ρυθμίσεων</Button>}
      </CardContent>
    </Card>
  );
};

export default DocketSettingsCard;
